import type { OrganizationTypeWithLinkedProfessions } from '@common/enums';
import { isNonNullable, nonNullable } from '@common/utils/nonNullableTypeUtils';
import type { Profession } from '../../common/enums/profession';
import type { ValidationErrors } from './validation';
import {
    identifierDeclarationAllowed,
    identifierDeclarationAllowedByOrganizationType,
    identifierDeclarationOrSupervisorRequired,
    identifierDeclarationOrSupervisorRequiredByOrganizationType,
    identifierDeclarationRequired,
    identifierDeclarationRequiredByOrganizationType,
    identifierRegistrationBigAllowed,
    identifierRegistrationBigAllowedByOrganizationType,
    identifierRegistrationKpAllowed,
    identifierRegistrationKpRequiredByOrganizationType,
    professionSpecialismCodes,
    specialismRequired,
    supervisorAllowed,
    supervisorAllowedByOrganizationType,
    supervisorRequired,
    supervisorRequiredByOrganizationType,
    zorgmailNummerAllowed,
    zorgmailNummerAllowedByOrganizationType,
    zorgmailNummerRequired,
    zorgmailNummerRequiredByOrganizationType,
} from '../../common/validation/utils/accountFieldsRequirementsByProfession';

interface IPractitioner {
    id?: string;
    identifierDeclaration?: string | null;
    identifierRegistrationBig?: string | null;
    professions?: Profession[];
}

export function isIdentifierRegistrationBigAllowed(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(identifierRegistrationBigAllowedByOrganizationType[organizationType]).includes(
                      profession
                  )
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => identifierRegistrationBigAllowed.includes(profession)));
}

export function isIdentifierRegistrationKpAllowed(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(identifierRegistrationKpRequiredByOrganizationType[organizationType]).includes(
                      profession
                  )
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => identifierRegistrationKpAllowed.includes(profession)));
}

export function isIdentifierDeclarationAllowed(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(identifierDeclarationAllowedByOrganizationType[organizationType]).includes(profession)
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => identifierDeclarationAllowed.includes(profession)));
}

export function isIdentifierDeclarationRequired(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(identifierDeclarationRequiredByOrganizationType[organizationType]).includes(profession)
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => identifierDeclarationRequired.includes(profession)));
}

export function isSupervisorRequired(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(supervisorRequiredByOrganizationType[organizationType]).includes(profession)
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => supervisorRequired.includes(profession)));
}

export function isSupervisorAllowed(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(supervisorAllowedByOrganizationType[organizationType]).includes(profession)
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => supervisorAllowed.includes(profession)));
}

export function isIdentifierDeclarationOrSupervisorRequired(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(identifierDeclarationOrSupervisorRequiredByOrganizationType[organizationType]).includes(
                      profession
                  )
              )
          )
        : Boolean(
              practitionerProfessions?.some((profession) =>
                  identifierDeclarationOrSupervisorRequired.includes(profession)
              )
          );
}

export function isZorgmailNummerRequired(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(zorgmailNummerRequiredByOrganizationType[organizationType]).includes(profession)
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => zorgmailNummerRequired.includes(profession)));
}

export function isZorgmailNummerAllowed(
    practitionerProfessions: Profession[] | undefined,
    organizationType?: OrganizationTypeWithLinkedProfessions | null
) {
    return organizationType
        ? Boolean(
              practitionerProfessions?.some((profession) =>
                  Object.values(zorgmailNummerAllowedByOrganizationType[organizationType]).includes(profession)
              )
          )
        : Boolean(practitionerProfessions?.some((profession) => zorgmailNummerAllowed.includes(profession)));
}

export function isSpecialismRequired(practitionerProfession: Profession | undefined | null) {
    return isNonNullable(practitionerProfession) && specialismRequired.includes(practitionerProfession);
}

export function getErrorsWithIdentifierValidation(newErrors: ValidationErrors, practitioner?: IPractitioner) {
    if (practitioner?.professions) {
        if (isIdentifierDeclarationRequired(practitioner.professions) && !practitioner.identifierDeclaration) {
            newErrors = newErrors.set('identifierDeclaration', 'requiredForRole');
        }
    }

    return newErrors;
}

export function getSpecialismCodeByProfession(profession: Profession | null, specialismCode: string | null) {
    if (!profession) {
        return null;
    }

    if (professionSpecialismCodes.has(profession)) {
        return nonNullable(professionSpecialismCodes.get(profession));
    }

    if (isSpecialismRequired(profession)) {
        return specialismCode;
    }

    return null;
}
